import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { SubscriptionPlanView } from "@/lib/api/types";
import { ADMIN_ROLES } from "@/lib/auth/admin-roles";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { useSubscriptionsQuery } from "@/shared/services/useSubscriptionsQuery";
import { PageHeaderActions } from "@/shared/layout/PageHeaderActions";
import { Button, EmptyView, ErrorState, FilterChip, LoadingState } from "@/shared/rd";
import { SubscriptionPlanCard } from "../components/SubscriptionPlanCard";
import { SubscriptionPlanEditor } from "../components/SubscriptionPlanEditor";
import { SubscriptionPlanDeleteModal } from "../components/SubscriptionPlanDeleteModal";
import { isFreePlanType, isPaidPlanType } from "../lib/plan-display";

type FilterKey = "active" | "inactive" | "paid" | "free";

type AdminSubscriptionsPageClientProps = {
  locale: string;
  searchParams: Partial<Record<FilterKey, string>>;
};

function hasFlag(value?: string) {
  return value === "true";
}

const FILTER_LABELS: Record<FilterKey, string> = {
  active: keys.admin.subscriptionFilterActive,
  inactive: keys.admin.subscriptionFilterInactive,
  paid: keys.admin.subscriptionFilterPaid,
  free: keys.admin.subscriptionFilterFree,
};

/** The plan catalogue: filter chips over a grid of plan cards. */
export function SubscriptionsPage({ locale, searchParams }: AdminSubscriptionsPageClientProps) {
  const t = useTranslations();
  const navigate = useNavigate();
  const { data: session, isLoading: isSessionLoading } = useSessionQuery();
  const { data, isLoading, error, refetch } = useSubscriptionsQuery();
  // `form.key` remounts the drawer per opening so its fields start fresh.
  const [form, setForm] = useState<{ open: boolean; plan: SubscriptionPlanView | null; key: number }>({
    open: false,
    plan: null,
    key: 0,
  });
  const [deleting, setDeleting] = useState<SubscriptionPlanView | null>(null);

  const elevatedAdminRoles = [ADMIN_ROLES.ADMIN, ADMIN_ROLES.SUPER_ADMIN];
  const canManagePlans =
    session?.admin?.roles?.some((role) =>
      elevatedAdminRoles.includes(role as (typeof elevatedAdminRoles)[number]),
    ) ?? false;

  const openForm = (plan: SubscriptionPlanView | null) =>
    setForm((current) => ({ open: true, plan, key: current.key + 1 }));
  const closeForm = () => setForm((current) => ({ ...current, open: false }));

  const createAction = canManagePlans ? (
    <PageHeaderActions>
      <Button variant="primary" size="sm" icon={Plus} onClick={() => openForm(null)}>
        {t(keys.admin.subscriptionCreateAction)}
      </Button>
    </PageHeaderActions>
  ) : null;

  const pageName = t(keys.admin.subscriptionsTitle).toLocaleLowerCase(locale);

  if (isSessionLoading || isLoading) {
    return (
      <LoadingState
        title={t(keys.shell.loadingTitle, { page: pageName })}
        sub={t(keys.shell.loadingSub)}
        rows={4}
      />
    );
  }

  if (error || !data) {
    return (
      <ErrorState
        title={t(keys.shell.errorTitle, { page: pageName })}
        sub={t(keys.shell.errorSub)}
        onRetry={() => void refetch()}
        retryLabel={t(keys.shell.retry)}
      />
    );
  }

  const flags: Record<FilterKey, boolean> = {
    active: hasFlag(searchParams.active),
    inactive: hasFlag(searchParams.inactive),
    paid: hasFlag(searchParams.paid),
    free: hasFlag(searchParams.free),
  };
  // Only a managing admin sees inactive plans, so only they get that filter.
  const filterKeys: FilterKey[] = canManagePlans
    ? ["active", "inactive", "paid", "free"]
    : ["active", "paid", "free"];

  const setFilters = (next: Record<FilterKey, boolean>) => {
    const query = new URLSearchParams();
    (Object.keys(next) as FilterKey[]).forEach((key) => {
      if (next[key]) query.set(key, "true");
    });
    const qs = query.toString();
    navigate(`/${locale}/admin/subscriptions${qs ? `?${qs}` : ""}`);
  };

  const statusFiltered = flags.active || flags.inactive;
  const typeFiltered = flags.paid || flags.free;

  const filteredPlans = data.items.filter((plan) => {
    if (!canManagePlans && !plan.isActive) return false;
    if (statusFiltered) {
      if (plan.isActive && !flags.active) return false;
      if (!plan.isActive && !flags.inactive) return false;
    }
    if (typeFiltered) {
      if (isPaidPlanType(plan.type) && !flags.paid) return false;
      if (isFreePlanType(plan.type) && !flags.free) return false;
    }
    return true;
  });

  const anyFilter = Object.values(flags).some(Boolean);
  const clearFilters = () => setFilters({ active: false, inactive: false, paid: false, free: false });

  return (
    <>
      {createAction}

      <div className="rd-toolbar">
        {filterKeys.map((key) => (
          <FilterChip key={key} active={flags[key]} onClick={() => setFilters({ ...flags, [key]: !flags[key] })}>
            {t(FILTER_LABELS[key])}
          </FilterChip>
        ))}
        <span style={{ fontSize: 13, color: "var(--rd-text-tertiary)", marginInlineStart: 4 }}>
          {t("admin.subscriptionPlansCount", { count: filteredPlans.length })}
        </span>
        {anyFilter ? (
          <Button variant="ghost" size="sm" muted onClick={clearFilters}>
            {t(keys.common.clearFilters)}
          </Button>
        ) : null}
      </div>

      {filteredPlans.length === 0 ? (
        <div className="rd-card">
          {anyFilter ? (
            <EmptyView
              title={t("admin.subscriptionPlansFilteredEmptyTitle")}
              sub={t("admin.subscriptionPlansFilteredEmptySub")}
              action={
                <Button size="sm" onClick={clearFilters}>
                  {t(keys.common.clearFilters)}
                </Button>
              }
            />
          ) : (
            <EmptyView
              title={t(keys.admin.subscriptionsEmptySubtitle)}
              sub={t("admin.subscriptionPlansEmptySub")}
              action={
                canManagePlans ? (
                  <Button size="sm" icon={Plus} onClick={() => openForm(null)}>
                    {t(keys.admin.subscriptionCreateAction)}
                  </Button>
                ) : undefined
              }
            />
          )}
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 300px), 1fr))",
            gap: 12,
          }}
        >
          {filteredPlans.map((plan) => (
            <SubscriptionPlanCard
              key={plan.id}
              plan={plan}
              canEdit={canManagePlans}
              onEdit={() => openForm(plan)}
              onDelete={() => setDeleting(plan)}
            />
          ))}
        </div>
      )}

      {canManagePlans ? (
        <>
          <SubscriptionPlanEditor
            key={form.key}
            open={form.open}
            plan={form.plan}
            onClose={closeForm}
          />
          <SubscriptionPlanDeleteModal plan={deleting} onClose={() => setDeleting(null)} />
        </>
      ) : null}
    </>
  );
}
